import { useState } from 'react'
import { useAuthStore } from '../stores/auth'
import {
    useActiveWorkspaceId,
    useCanManage,
    useWorkspaceRole,
} from '../stores/workspace'
import { useT } from '../i18n/context'
import { GroupPageLayout } from '../components/GroupPageLayout'
import type { PageTabItem } from '../components/PageTabs'
import {
    useAddressMutations,
    useAddresses,
    useInvites,
    useMemberMutations,
    useMembers,
    useRevokeInvite,
    useUpdateWorkspace,
    useWorkspaceDelete,
    workspaceRoleLabels,
    type AddressBody,
} from '../hooks/useWorkspaceApi'
import type {
    Address,
    WorkspaceInvite,
    WorkspaceMember,
    WorkspaceRole,
} from '../types/api'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody, CardHeader } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { EmptyState } from '../components/ui/EmptyState'
import { Field, Input, Select } from '../components/ui/Field'
import { PageLoader } from '../components/ui/Spinner'
import { Checkbox } from '../components/ui/Checkbox'
import { formatDate, fromLocalInputValue, formatWeekdayLong } from '../lib/format'
import { DateInput, TimeInput } from '../components/ui/DateTimeInputs'
import { errText } from '../lib/errors'
import { toast } from '../stores/toast'
import {
    useOvertimeTiers,
    useOvertimeTiersMutation,
    useWorkingTimeSettings,
    useWorkingTimeSettingsMutation,
} from '../hooks/useWorkingTimeApi'

const tabs: PageTabItem[] = [
    { to: '/workspace/clients', labelKey: 'workspace.tabClients' },
    { to: '/workspace/members', labelKey: 'workspace.tabMembers' },
    { to: '/workspace/addresses', labelKey: 'workspace.tabAddresses' },
    { to: '/workspace/general', labelKey: 'workspace.tabGeneral' },
]

const roleTone: Record<WorkspaceRole, 'indigo' | 'blue' | 'gray'> = {
    OWNER: 'indigo',
    ADMIN: 'blue',
    MEMBER: 'gray',
}

const roleRank: Record<WorkspaceRole, number> = {
    OWNER: 0,
    ADMIN: 1,
    MEMBER: 2,
}

export function WorkspacePage() {
    return (
        <GroupPageLayout
            titleKey='workspace.title'
            subtitleKey='workspace.subtitle'
            tabs={tabs}
        />
    )
}

// ---------- Allgemein ----------

/** ISO-Wochentags-Labels (1=Mo … 7=So) — 07.09.2026 ist ein Montag. */
const weekdayLabel = (isoDay: number) => {
    const d = new Date(2026, 8, 6 + isoDay)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return formatWeekdayLong(key)
}

/**
 * Arbeitszeit-Einstellungen des Workspace (KONZEPT_ARBEITSZEIT.md):
 * Arbeitstage, Sonntags-Regel, Feiertags-Faktor, Überstunden-Staffel
 * (F4). Das Nacht-Fenster folgt mit Phase 3 im selben Formular.
 */
function WorkingTimeSettingsCard({ workspaceId }: { workspaceId: number }) {
    const t = useT()
    const settings = useWorkingTimeSettings(workspaceId)
    const save = useWorkingTimeSettingsMutation(workspaceId)
    const tiers = useOvertimeTiers(workspaceId)
    const saveTiers = useOvertimeTiersMutation(workspaceId)

    const [initializedFor, setInitializedFor] = useState<number | null>(null)
    const [workdays, setWorkdays] = useState<number[]>([1, 2, 3, 4, 5])
    const [sundayHoliday, setSundayHoliday] = useState(true)
    const [holidayFactor, setHolidayFactor] = useState('2')
    const [nightFrom, setNightFrom] = useState('20:00')
    const [nightTo, setNightTo] = useState('06:00')
    const [nightFactor, setNightFactor] = useState('1.25')
    const [tierRows, setTierRows] = useState<{ fromHours: string; factor: string }[]>([])

    const data = settings.data
    if (data && initializedFor !== workspaceId) {
        setInitializedFor(workspaceId)
        setWorkdays(data.workdays)
        setSundayHoliday(data.sundayHoliday)
        setHolidayFactor(String(data.holidayFactor))
        setNightFrom(data.nightFrom)
        setNightTo(data.nightTo)
        setNightFactor(String(data.nightFactor))
    }
    const tiersKey = (tiers.data ?? [])
        .map(tier => `${tier.fromHours}:${tier.factor}`)
        .join('|')
    const [tiersInitializedFor, setTiersInitializedFor] = useState<
        string | null
    >(null)
    if (tiersKey !== '' && tiersInitializedFor !== tiersKey) {
        setTiersInitializedFor(tiersKey)
        setTierRows(
            (tiers.data ?? []).map(tier => ({
                fromHours: String(tier.fromHours),
                factor: String(tier.factor),
            })),
        )
    }

    const toggleDay = (day: number) => {
        setWorkdays(prev =>
            prev.includes(day)
                ? prev.filter(d => d !== day)
                : [...prev, day].sort((a, b) => a - b),
        )
    }

    if (settings.isLoading) return <PageLoader />
    if (settings.isError || !data) {
        return (
            <Card>
                <CardHeader title={t('workspace.workingTimeTitle')} />
                <CardBody>
                    <EmptyState
                        title={t('workingTime.loadErrorTitle')}
                        description={settings.isError ? errText(settings.error) : undefined}
                    />
                </CardBody>
            </Card>
        )
    }

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        save.mutate(
            {
                ...data,
                workdays,
                sundayHoliday,
                holidayFactor: Number(holidayFactor.replace(',', '.') || '2'),
                nightFrom,
                nightTo,
                nightFactor: Number(nightFactor.replace(',', '.') || '1.25'),
            },
            {
                onSuccess: () =>
                    toast.success(t('workspace.workingTimeSaved')),
                onError: err => toast.error(errText(err)),
            },
        )
        // Staffel separat speichern (eigener Endpoint), nur wenn Zeilen da sind
        const parsed = tierRows
            .map(row => ({
                fromHours: Number(row.fromHours.replace(',', '.')),
                factor: Number(row.factor.replace(',', '.')),
            }))
            .filter(row => Number.isFinite(row.fromHours) && Number.isFinite(row.factor))
        if (parsed.length > 0) {
            saveTiers.mutate(parsed, {
                onError: err => toast.error(errText(err)),
            })
        }
    }

    return (
        <Card>
            <CardHeader title={t('workspace.workingTimeTitle')} />
            <CardBody>
                <form onSubmit={submit} className='space-y-5'>
                    <Field label={t('workspace.workingTimeWorkdays')}>
                        <div className='flex flex-wrap gap-x-4 gap-y-2'>
                            {[1, 2, 3, 4, 5, 6, 7].map(day => (
                                <label
                                    key={day}
                                    className='flex cursor-pointer items-center gap-1.5 text-sm'
                                >
                                    <input
                                        type='checkbox'
                                        checked={workdays.includes(day)}
                                        onChange={() => toggleDay(day)}
                                        className='h-4 w-4 rounded border-slate-300 accent-indigo-600 dark:border-slate-600'
                                    />
                                    {weekdayLabel(day)}
                                </label>
                            ))}
                        </div>
                    </Field>
                    <p className='-mt-3 text-xs text-slate-500 dark:text-slate-400'>
                        {t('workspace.workingTimeWorkdaysHint')}
                    </p>

                    <div className='grid gap-3 sm:grid-cols-2'>
                        <Checkbox
                            checked={sundayHoliday}
                            onChange={setSundayHoliday}
                            label={t('workingTime.sundayHolidayLabel')}
                        />
                        <Field
                            label={t('workingTime.holidayFactorSettingsLabel')}
                        >
                            <Input
                                type='number'
                                min='0.01'
                                step='0.25'
                                value={holidayFactor}
                                onChange={e => setHolidayFactor(e.target.value)}
                            />
                        </Field>
                    </div>

                    <div className='grid gap-3 sm:grid-cols-3'>
                        <Field label={t('workingTime.nightFromLabel')}>
                            <TimeInput value={nightFrom} onChange={setNightFrom} />
                        </Field>
                        <Field label={t('workingTime.nightToLabel')}>
                            <TimeInput value={nightTo} onChange={setNightTo} />
                        </Field>
                        <Field label={t('workingTime.nightFactorLabel')}>
                            <Input
                                type='number'
                                min='0.01'
                                step='0.05'
                                value={nightFactor}
                                onChange={e => setNightFactor(e.target.value)}
                            />
                        </Field>
                    </div>
                    <p className='-mt-3 text-xs text-slate-500 dark:text-slate-400'>
                        {t('workingTime.nightHint')}
                    </p>

                    <div>
                        <div className='mb-1 text-sm font-medium text-slate-700 dark:text-slate-200'>
                            {t('workingTime.tiersTitle')}
                        </div>
                        <p className='mb-2 text-xs text-slate-500 dark:text-slate-400'>
                            {t('workingTime.tiersHint')}
                        </p>
                        <div className='space-y-2'>
                            {tierRows.map((row, i) => (
                                <div key={i} className='flex items-end gap-2'>
                                    <Field label={t('workingTime.tierFrom')}>
                                        <Input
                                            inputMode='decimal'
                                            value={row.fromHours}
                                            onChange={e =>
                                                setTierRows(prev =>
                                                    prev.map((r, idx) =>
                                                        idx === i
                                                            ? { ...r, fromHours: e.target.value }
                                                            : r,
                                                    ),
                                                )
                                            }
                                            className='w-24'
                                        />
                                    </Field>
                                    <Field label={t('workingTime.tierFactor')}>
                                        <Input
                                            inputMode='decimal'
                                            value={row.factor}
                                            onChange={e =>
                                                setTierRows(prev =>
                                                    prev.map((r, idx) =>
                                                        idx === i
                                                            ? { ...r, factor: e.target.value }
                                                            : r,
                                                    ),
                                                )
                                            }
                                            className='w-24'
                                        />
                                    </Field>
                                    <Button
                                        type='button'
                                        variant='ghost'
                                        size='sm'
                                        onClick={() =>
                                            setTierRows(prev =>
                                                prev.filter((_, idx) => idx !== i),
                                            )
                                        }
                                    >
                                        {t('common.delete')}
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <Button
                            type='button'
                            variant='secondary'
                            size='sm'
                            className='mt-2'
                            onClick={() =>
                                setTierRows(prev => [
                                    ...prev,
                                    { fromHours: '', factor: '1.0' },
                                ])
                            }
                        >
                            {t('workingTime.tierAdd')}
                        </Button>
                    </div>

                    <div className='flex justify-end'>
                        <Button
                            type='submit'
                            loading={save.isPending || saveTiers.isPending}
                            disabled={workdays.length === 0}
                        >
                            {t('common.save')}
                        </Button>
                    </div>
                </form>
            </CardBody>
        </Card>
    )
}

/** Sub-Route /workspace/general — Rechnungsdaten + Arbeitszeit-Einstellungen. */
// ---------- Workspace löschen (WP4b: 3 Schritte, Lösch-Code per Mail) ----------

/**
 * Nur OWNER. Schritt 1: Warnung + Workspace-Name eintippen; Schritt 2:
 * 6-stelligen Lösch-Code anfordern (Mail, 15 Min, single-use); Schritt 3:
 * Code eingeben + endgültig löschen hinter ConfirmDialog.
 */
function DeleteWorkspaceCard({ workspaceName }: { workspaceName: string }) {
    const t = useT()
    const user = useAuthStore(s => s.user)
    const workspaceId = useActiveWorkspaceId()
    const { requestCode, confirmDelete } = useWorkspaceDelete(workspaceId)

    const [nameConfirm, setNameConfirm] = useState('')
    const [codeRequested, setCodeRequested] = useState(false)
    const [code, setCode] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)

    const nameMatches = nameConfirm.trim() === workspaceName
    const codeValid = /^\d{6}$/.test(code.trim())

    const request = () =>
        requestCode.mutate(undefined, {
            onSuccess: () => {
                setCodeRequested(true)
                toast.info(t('workspace.codeSent', { email: user?.email ?? '' }))
            },
            onError: err => toast.error(errText(err)),
        })

    const doDelete = () =>
        confirmDelete.mutate(code.trim(), {
            onSuccess: () => toast.success(t('workspace.deleted')),
            onError: err => toast.error(errText(err)),
            onSettled: () => setConfirmOpen(false),
        })

    return (
        <Card>
            <CardHeader title={t('workspace.dangerTitle')} />
            <CardBody className='space-y-4'>
                <p className='text-sm text-slate-600 dark:text-slate-300'>
                    {t('workspace.dangerDesc')}
                </p>

                <Field label={t('workspace.nameConfirmLabel')}>
                    <Input
                        value={nameConfirm}
                        onChange={e => setNameConfirm(e.target.value)}
                        placeholder={workspaceName}
                        autoComplete='off'
                    />
                </Field>

                <div className='flex flex-wrap items-center gap-2'>
                    <Button
                        type='button'
                        variant='secondary'
                        disabled={!nameMatches}
                        loading={requestCode.isPending}
                        onClick={request}
                    >
                        {codeRequested
                            ? t('workspace.requestCodeAgain')
                            : t('workspace.requestCode')}
                    </Button>
                </div>

                {codeRequested && (
                    <div className='space-y-3 rounded-lg border border-red-200 bg-red-50/60 p-3 dark:border-red-900/60 dark:bg-red-950/30'>
                        <Field label={t('workspace.codeLabel')}>
                            <Input
                                value={code}
                                onChange={e =>
                                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                                }
                                placeholder={t('workspace.codePlaceholder')}
                                inputMode='numeric'
                                autoComplete='one-time-code'
                            />
                        </Field>
                        <div className='flex justify-end'>
                            <Button
                                type='button'
                                variant='danger'
                                disabled={!codeValid}
                                onClick={() => setConfirmOpen(true)}
                            >
                                {t('workspace.deleteButton')}
                            </Button>
                        </div>
                    </div>
                )}
            </CardBody>

            <ConfirmDialog
                open={confirmOpen}
                message={t('workspace.deleteConfirm', { name: workspaceName })}
                confirmLabel={t('workspace.deleteButton')}
                loading={confirmDelete.isPending}
                onConfirm={doDelete}
                onClose={() => setConfirmOpen(false)}
            />
        </Card>
    )
}

export function GeneralTab() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const canManage = useCanManage()
    const myRole = useWorkspaceRole()
    const user = useAuthStore(s => s.user)
    const workspace = user?.workspaces.find(
        w => w.workspace.id === workspaceId,
    )?.workspace
    const update = useUpdateWorkspace(workspaceId)

    const [initializedFor, setInitializedFor] = useState<number | null>(null)
    const [name, setName] = useState('')
    const [taxNumber, setTaxNumber] = useState('')

    // Reset synchron im Render, Key = Workspace-ID (Switcher-Wechsel)
    if (workspace && initializedFor !== workspace.id) {
        setInitializedFor(workspace.id)
        setName(workspace.name)
        setTaxNumber(workspace.taxNumber ?? '')
    }

    if (workspaceId === null || !workspace) return null

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        update.mutate(
            {
                name: name.trim(),
                taxNumber: taxNumber.trim() || null,
            },
            {
                onSuccess: () => toast.success(t('workspace.updated')),
                onError: err => toast.error(errText(err)),
            },
        )
    }

    return (
        <div className='space-y-4'>
            <Card>
                <CardHeader title={t('workspace.billingData')} />
                <CardBody>
                    <form onSubmit={submit} className='space-y-4'>
                        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                            <Field label={t('workspace.wsName')}>
                                <Input
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                    maxLength={100}
                                />
                            </Field>
                            <Field label={t('workspace.vatLabel')}>
                                <Input
                                    value={taxNumber}
                                    onChange={e => setTaxNumber(e.target.value)}
                                    placeholder={t('workspace.vatPlaceholder')}
                                    maxLength={191}
                                />
                            </Field>
                        </div>
                        <p className='text-xs text-slate-500 dark:text-slate-400'>
                            {t('workspace.vatHint')}
                        </p>
                        <div className='flex justify-end'>
                            <Button
                                type='submit'
                                loading={update.isPending}
                                disabled={
                                    name.trim() === workspace.name &&
                                    (taxNumber.trim() || null) ===
                                        (workspace.taxNumber ?? null)
                                }
                            >
                                {t('common.save')}
                            </Button>
                        </div>
                    </form>
                </CardBody>
            </Card>
            {canManage && <WorkingTimeSettingsCard workspaceId={workspaceId} />}
            {myRole === 'OWNER' && (
                <DeleteWorkspaceCard
                    key={workspace.id}
                    workspaceName={workspace.name}
                />
            )}
        </div>
    )
}

// ---------- Mitglieder ----------

/** Sub-Route /workspace/members — Mitglieder, Rollen, Einladungen. */
export function MembersTab() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const me = useAuthStore(s => s.user)
    const myRole = useWorkspaceRole()
    const canManage = myRole === 'OWNER' || myRole === 'ADMIN'
    const members = useMembers(workspaceId)
    const invites = useInvites(workspaceId)
    const { add, update, remove } = useMemberMutations(workspaceId)
    const revoke = useRevokeInvite(workspaceId)

    const [inviteEmail, setInviteEmail] = useState('')
    const [inviteRole, setInviteRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER')
    const [contractDialog, setContractDialog] = useState<WorkspaceMember | null>(
        null,
    )
    const [removeIntent, setRemoveIntent] = useState<WorkspaceMember | null>(
        null,
    )
    const [promoteIntent, setPromoteIntent] = useState<WorkspaceMember | null>(
        null,
    )
    const [revokeIntent, setRevokeIntent] = useState<WorkspaceInvite | null>(
        null,
    )

    if (workspaceId === null) return null

    const sorted = [...(members.data ?? [])].sort(
        (a, b) =>
            roleRank[a.role] - roleRank[b.role] ||
            a.user.name.localeCompare(b.user.name),
    )

    const canRemove = (m: WorkspaceMember) => {
        if (me && m.userId === me.id) return true // Selbstaustritt
        if (myRole === 'OWNER') return true
        if (myRole === 'ADMIN') return m.role === 'MEMBER'
        return false
    }

    const removeMember = (m: WorkspaceMember) => {
        const self = me?.id === m.userId
        remove.mutate(m.userId, {
            onSuccess: () => {
                toast.success(
                    self
                        ? t('workspace.leftToast')
                        : t('workspace.removedToast', { name: m.user.name }),
                )
                setRemoveIntent(null)
            },
            onError: err => toast.error(errText(err)),
        })
    }

    const changeRole = (m: WorkspaceMember, role: WorkspaceRole) => {
        if (role === m.role) return
        if (role === 'OWNER') {
            setPromoteIntent(m)
            return
        }
        update.mutate(
            { userId: m.userId, role },
            {
                onSuccess: () =>
                    toast.success(
                        t('workspace.roleChanged', {
                            name: m.user.name,
                            role: t(workspaceRoleLabels[role]),
                        }),
                    ),
                onError: err => toast.error(errText(err)),
            },
        )
    }

    const saveRate = (m: WorkspaceMember, value: number | null) => {
        update.mutate(
            { userId: m.userId, hourlyRate: value },
            {
                onSuccess: () => toast.success(t('workspace.rateSaved')),
                onError: err => toast.error(errText(err)),
            },
        )
    }

    // Vertragsdaten der Arbeitszeit-Verwaltung: Mitglied selbst oder
    // OWNER/ADMIN (Vorträge nur OWNER/ADMIN — Backend prüft mit)
    const canEditContract = (m: WorkspaceMember) =>
        canManage || me?.id === m.userId

    const saveContract = (
        m: WorkspaceMember,
        patch: Partial<
            Pick<
                WorkspaceMember,
                | 'weeklyTargetHours'
                | 'vacationDaysPerYear'
                | 'coreTimeFrom'
                | 'coreTimeTo'
                | 'workingTimeStart'
                | 'autoBreak'
                | 'overtimeCarriedHours'
                | 'vacationCarriedDays'
            >
        >,
    ) => {
        update.mutate(
            { userId: m.userId, ...patch },
            {
                onSuccess: () =>
                    toast.success(t('workingTime.contractSaved')),
                onError: err => toast.error(errText(err)),
            },
        )
    }

    const sendInvite = (e: React.FormEvent) => {
        e.preventDefault()
        const email = inviteEmail.trim()
        if (!email) return
        add.mutate(
            { email, role: inviteRole },
            {
                onSuccess: res => {
                    if (res.added) {
                        toast.success(t('workspace.addedDirect', { email }))
                    } else {
                        toast.info(t('workspace.inviteSent', { email }))
                    }
                    setInviteEmail('')
                    setInviteRole('MEMBER')
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    return (
        <div className='space-y-4'>
            <Card>
                <CardHeader title={t('workspace.members')} />
                {members.isLoading ? (
                    <PageLoader />
                ) : sorted.length === 0 ? (
                    <EmptyState title={t('workspace.noMembers')} />
                ) : (
                    <CardBody className='p-0'>
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                        <th className='px-4 py-2.5'>{t('workspace.name')}</th>
                                        <th className='px-4 py-2.5'>{t('workspace.email')}</th>
                                        <th className='px-4 py-2.5'>{t('workspace.role')}</th>
                                        <th className='px-4 py-2.5 text-right'>
                                            {t('workspace.rateLabel')}
                                        </th>
                                        <th className='px-4 py-2.5 text-right'>
                                            {t('workingTime.setupWeeklyHoursShort')}
                                        </th>
                                        <th className='px-4 py-2.5 text-right'>
                                            {t('workingTime.setupVacationDaysShort')}
                                        </th>
                                        <th className='px-4 py-2.5' />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.map(m => (
                                        <tr
                                            key={m.userId}
                                            className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                        >
                                            <td className='px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100'>
                                                {m.user.name}
                                                {me?.id === m.userId && (
                                                    <span className='ml-1.5 text-xs font-normal text-slate-500 dark:text-slate-400'>
                                                        {t('workspace.you')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className='px-4 py-2.5 text-slate-500 dark:text-slate-400'>
                                                {m.user.email}
                                            </td>
                                            <td className='px-4 py-2.5'>
                                                {myRole === 'OWNER' ? (
                                                    <Select
                                                        value={m.role}
                                                        onChange={e =>
                                                            changeRole(
                                                                m,
                                                                e.target
                                                                    .value as WorkspaceRole,
                                                            )
                                                        }
                                                        className='w-32 py-1 text-xs'
                                                        disabled={
                                                            update.isPending
                                                        }
                                                    >
                                                        {(
                                                            Object.keys(
                                                                workspaceRoleLabels,
                                                            ) as WorkspaceRole[]
                                                        ).map(r => (
                                                            <option
                                                                key={r}
                                                                value={r}
                                                            >
                                                                {t(
                                                                    workspaceRoleLabels[
                                                                        r
                                                                    ],
                                                                )}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <Badge tone={roleTone[m.role]}>
                                                        {t(
                                                            workspaceRoleLabels[
                                                                m.role
                                                            ],
                                                        )}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className='px-4 py-2.5 text-right'>
                                                {canManage ? (
                                                    <MemberRateInput
                                                        key={`${m.userId}-${m.hourlyRate ?? 'null'}`}
                                                        rate={m.hourlyRate}
                                                        onSave={v =>
                                                            saveRate(m, v)
                                                        }
                                                    />
                                                ) : (
                                                    <span className='tabular-nums text-slate-600 dark:text-slate-300'>
                                                        {m.hourlyRate !== null
                                                            ? m.hourlyRate
                                                                  .toFixed(2)
                                                                  .replace(
                                                                      '.',
                                                                      ',',
                                                                  )
                                                            : '—'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className='px-4 py-2.5 text-right'>
                                                {canEditContract(m) ? (
                                                    <MemberRateInput
                                                        key={`${m.userId}-wt-${m.weeklyTargetHours ?? 'null'}`}
                                                        rate={m.weeklyTargetHours}
                                                        onSave={v =>
                                                            saveContract(m, {
                                                                weeklyTargetHours:
                                                                    v,
                                                            })
                                                        }
                                                    />
                                                ) : (
                                                    <span className='tabular-nums text-slate-600 dark:text-slate-300'>
                                                        {m.weeklyTargetHours ??
                                                            '—'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className='px-4 py-2.5 text-right'>
                                                {canEditContract(m) ? (
                                                    <MemberRateInput
                                                        key={`${m.userId}-vac-${m.vacationDaysPerYear ?? 'null'}`}
                                                        rate={m.vacationDaysPerYear}
                                                        onSave={v =>
                                                            saveContract(m, {
                                                                vacationDaysPerYear:
                                                                    v,
                                                            })
                                                        }
                                                    />
                                                ) : (
                                                    <span className='tabular-nums text-slate-600 dark:text-slate-300'>
                                                        {m.vacationDaysPerYear ??
                                                            '—'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className='whitespace-nowrap px-4 py-2.5 text-right'>
                                                {canEditContract(m) && (
                                                    <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        onClick={() =>
                                                            setContractDialog(
                                                                m,
                                                            )
                                                        }
                                                    >
                                                        {t(
                                                            'workspace.contract',
                                                        )}
                                                    </Button>
                                                )}
                                                {canRemove(m) && (
                                                    <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        onClick={() =>
                                                            setRemoveIntent(m)
                                                        }
                                                    >
                                                        {me?.id === m.userId
                                                            ? t('workspace.leave')
                                                            : t('workspace.remove')}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardBody>
                )}
            </Card>

            {canManage && (
                <Card>
                    <CardHeader title={t('workspace.inviteTitle')} />
                    <CardBody>
                        <form
                            onSubmit={sendInvite}
                            className='flex flex-wrap items-end gap-2'
                        >
                            <Field label={t('workspace.email')} className='flex-1'>
                                <Input
                                    type='email'
                                    value={inviteEmail}
                                    onChange={e =>
                                        setInviteEmail(e.target.value)
                                    }
                                    placeholder={t('workspace.inviteEmailPlaceholder')}
                                    required
                                />
                            </Field>
                            <Field label={t('workspace.role')}>
                                <Select
                                    value={inviteRole}
                                    onChange={e =>
                                        setInviteRole(e.target.value as
                                            | 'ADMIN'
                                            | 'MEMBER')
                                    }
                                    className='w-36'
                                >
                                    <option value='MEMBER'>{t('workspace.roleMember')}</option>
                                    {myRole === 'OWNER' && (
                                        <option value='ADMIN'>{t('workspace.roleAdmin')}</option>
                                    )}
                                </Select>
                            </Field>
                            <Button type='submit' loading={add.isPending}>
                                {t('workspace.invite')}
                            </Button>
                        </form>
                        <p className='mt-2 text-xs text-slate-500 dark:text-slate-400'>
                            {t('workspace.inviteHint')}
                        </p>
                    </CardBody>
                </Card>
            )}

            {canManage && (
                <Card>
                    <CardHeader title={t('workspace.openInvites')} />
                    {invites.isLoading ? (
                        <PageLoader />
                    ) : (invites.data ?? []).length === 0 ? (
                        <EmptyState
                            title={t('workspace.noInvites')}
                            description={t('workspace.noInvitesDesc')}
                        />
                    ) : (
                        <CardBody className='p-0'>
                            <div className='overflow-x-auto'>
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                            <th className='px-4 py-2.5'>
                                                {t('workspace.email')}
                                            </th>
                                            <th className='px-4 py-2.5'>
                                                {t('workspace.role')}
                                            </th>
                                            <th className='px-4 py-2.5'>
                                                {t('workspace.validUntil')}
                                            </th>
                                            <th className='px-4 py-2.5'>
                                                {t('workspace.invitedBy')}
                                            </th>
                                            <th className='px-4 py-2.5' />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(invites.data ?? []).map(inv => (
                                            <tr
                                                key={inv.id}
                                                className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                            >
                                                <td className='px-4 py-2.5 font-medium text-slate-700 dark:text-slate-200'>
                                                    {inv.email}
                                                </td>
                                                <td className='px-4 py-2.5'>
                                                    <Badge tone={roleTone[inv.role]}>
                                                        {t(
                                                            workspaceRoleLabels[
                                                                inv.role
                                                            ],
                                                        )}
                                                    </Badge>
                                                </td>
                                                <td className='px-4 py-2.5 whitespace-nowrap text-slate-500 dark:text-slate-400'>
                                                    {formatDate(inv.expiresAt)}
                                                </td>
                                                <td className='px-4 py-2.5 text-slate-500 dark:text-slate-400'>
                                                    {inv.invitedBy?.name ??
                                                    '—'}
                                                </td>
                                                <td className='px-4 py-2.5 text-right'>
                                                    <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        onClick={() =>
                                                            setRevokeIntent(inv)
                                                        }
                                                    >
                                                        {t('workspace.revoke')}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    )}
                </Card>
            )}

            {contractDialog !== null && (
                <MemberContractDialog
                    member={contractDialog}
                    canSetCarried={canManage}
                    saving={update.isPending}
                    onClose={() => setContractDialog(null)}
                    onSave={patch => {
                        saveContract(contractDialog, patch)
                        setContractDialog(null)
                    }}
                />
            )}

            <ConfirmDialog
                open={removeIntent !== null}
                message={
                    removeIntent
                        ? removeIntent.userId === me?.id
                            ? t('workspace.leaveConfirm')
                            : t('workspace.removeConfirm', {
                                  name: removeIntent.user.name,
                              })
                        : ''
                }
                confirmLabel={
                    removeIntent?.userId === me?.id
                        ? t('workspace.leave')
                        : t('workspace.remove')
                }
                loading={remove.isPending}
                onClose={() => setRemoveIntent(null)}
                onConfirm={() => removeIntent && removeMember(removeIntent)}
            />
            <ConfirmDialog
                open={promoteIntent !== null}
                message={
                    promoteIntent
                        ? t('workspace.promoteConfirm', {
                              name: promoteIntent.user.name,
                          })
                        : ''
                }
                confirmLabel={t('workspace.promote')}
                tone='primary'
                loading={update.isPending}
                onClose={() => setPromoteIntent(null)}
                onConfirm={() => {
                    if (!promoteIntent) return
                    update.mutate(
                        { userId: promoteIntent.userId, role: 'OWNER' },
                        {
                            onSuccess: () => {
                                toast.success(
                                    t('workspace.roleChanged', {
                                        name: promoteIntent.user.name,
                                        role: t(workspaceRoleLabels.OWNER),
                                    }),
                                )
                                setPromoteIntent(null)
                            },
                            onError: err => toast.error(errText(err)),
                        },
                    )
                }}
            />
            <ConfirmDialog
                open={revokeIntent !== null}
                message={
                    revokeIntent
                        ? t('workspace.revokeConfirm', {
                              email: revokeIntent.email,
                          })
                        : ''
                }
                confirmLabel={t('workspace.revoke')}
                loading={revoke.isPending}
                onClose={() => setRevokeIntent(null)}
                onConfirm={() => {
                    if (!revokeIntent) return
                    revoke.mutate(revokeIntent.id, {
                        onSuccess: () => {
                            toast.success(t('workspace.revoked'))
                            setRevokeIntent(null)
                        },
                        onError: err => toast.error(errText(err)),
                    })
                }}
            />
        </div>
    )
}

/**
 * Vertragsdaten eines Mitglieds (KONZEPT_ARBEITSZEIT.md F1): Starttermin,
 * Kernarbeitszeit-Fenster, Auto-Pause-Option; Vorträge nur OWNER/ADMIN.
 */
function MemberContractDialog({
    member,
    canSetCarried,
    saving,
    onClose,
    onSave,
}: {
    member: WorkspaceMember
    canSetCarried: boolean
    saving: boolean
    onClose: () => void
    onSave: (
        patch: Partial<
            Pick<
                WorkspaceMember,
                | 'coreTimeFrom'
                | 'coreTimeTo'
                | 'workingTimeStart'
                | 'autoBreak'
                | 'overtimeCarriedHours'
                | 'vacationCarriedDays'
            >
        >,
    ) => void
}) {
    const t = useT()
    const [initializedFor, setInitializedFor] = useState<number | null>(null)
    const [start, setStart] = useState('')
    const [coreFrom, setCoreFrom] = useState('')
    const [coreTo, setCoreTo] = useState('')
    const [autoBreak, setAutoBreak] = useState(false)
    const [overtimeCarry, setOvertimeCarry] = useState('0')
    const [vacationCarry, setVacationCarry] = useState('0')

    if (initializedFor !== member.userId) {
        setInitializedFor(member.userId)
        setStart(
            member.workingTimeStart
                ? member.workingTimeStart.slice(0, 10)
                : '',
        )
        setCoreFrom(member.coreTimeFrom ?? '')
        setCoreTo(member.coreTimeTo ?? '')
        setAutoBreak(member.autoBreak)
        setOvertimeCarry(String(member.overtimeCarriedHours))
        setVacationCarry(String(member.vacationCarriedDays))
    }

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        if (coreFrom && coreTo && coreFrom >= coreTo) {
            toast.error(t('workingTime.setupCoreTimeInvalid'))
            return
        }
        onSave({
            coreTimeFrom: coreFrom || null,
            coreTimeTo: coreTo || null,
            workingTimeStart: start
                ? fromLocalInputValue(`${start}T00:00`)
                : null,
            autoBreak,
            ...(canSetCarried
                ? {
                      overtimeCarriedHours: Number(
                          overtimeCarry.replace(',', '.') || '0',
                      ),
                      vacationCarriedDays: Number(
                          vacationCarry.replace(',', '.') || '0',
                      ),
                  }
                : {}),
        })
    }

    return (
        <Dialog
            open
            onClose={onClose}
            title={`${t('workspace.contract')} · ${member.user.name}`}
        >
            <form onSubmit={submit} className='space-y-4'>
                <Field label={t('workingTime.setupStart')}>
                    <DateInput value={start} onChange={setStart} />
                </Field>
                <div className='grid grid-cols-2 gap-3'>
                    <Field label={t('workingTime.setupCoreFrom')}>
                        <TimeInput value={coreFrom} onChange={setCoreFrom} />
                    </Field>
                    <Field label={t('workingTime.setupCoreTo')}>
                        <TimeInput value={coreTo} onChange={setCoreTo} />
                    </Field>
                </div>
                <Checkbox
                    checked={autoBreak}
                    onChange={setAutoBreak}
                    label={t('workingTime.autoBreakLabel')}
                    hint={t('workingTime.autoBreakHint')}
                />
                {canSetCarried && (
                    <div className='grid grid-cols-2 gap-3 rounded-lg bg-slate-50/60 p-3 dark:bg-slate-800/60'>
                        <Field label={t('workingTime.overtimeCarryLabel')}>
                            <Input
                                inputMode='decimal'
                                value={overtimeCarry}
                                onChange={e => setOvertimeCarry(e.target.value)}
                            />
                        </Field>
                        <Field label={t('workingTime.vacationCarryLabel')}>
                            <Input
                                inputMode='decimal'
                                value={vacationCarry}
                                onChange={e => setVacationCarry(e.target.value)}
                            />
                        </Field>
                        <p className='col-span-2 text-xs text-slate-500 dark:text-slate-400'>
                            {t('workingTime.carryHint')}
                        </p>
                    </div>
                )}
                <div className='flex justify-end gap-2'>
                    <Button type='button' variant='secondary' onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button type='submit' loading={saving}>
                        {t('common.save')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

/** Stundensatz inline editierbar — Speichern bei Blur/Enter, leer = null. */
function MemberRateInput({
    rate,
    onSave,
}: {
    rate: number | null
    onSave: (value: number | null) => void
}) {
    const [value, setValue] = useState(rate !== null ? String(rate) : '')

    const commit = () => {
        const trimmed = value.trim().replace(',', '.')
        if (trimmed === '') {
            if (rate !== null) onSave(null)
            return
        }
        const parsed = Number(trimmed)
        if (!Number.isFinite(parsed) || parsed === rate) return
        onSave(parsed)
    }

    return (
        <Input
            inputMode='decimal'
            value={value}
            onChange={e => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur()
                if (e.key === 'Escape') {
                    setValue(rate !== null ? String(rate) : '')
                    e.currentTarget.blur()
                }
            }}
            placeholder='—'
            className='w-24 py-1 text-right text-sm'
        />
    )
}

// ---------- Adressen ----------

/** Sub-Route /workspace/addresses — Absender-Adressen fürs Rechnungs-PDF. */
export function AddressesTab() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const myRole = useWorkspaceRole()
    const canManage = myRole === 'OWNER' || myRole === 'ADMIN'
    const addresses = useAddresses(workspaceId)
    const { remove } = useAddressMutations(workspaceId)
    const [dialog, setDialog] = useState<Address | 'new' | null>(null)
    const [removeIntent, setRemoveIntent] = useState<Address | null>(null)

    if (workspaceId === null) return null

    const removeAddress = (a: Address) => {
        remove.mutate(a.id, {
            onSuccess: () => {
                toast.success(t('workspace.addressDeleted'))
                setRemoveIntent(null)
            },
            onError: err => toast.error(errText(err)),
        })
    }

    return (
        <div className='space-y-4'>
            <div className='flex items-center gap-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 px-4 py-2.5 text-sm text-indigo-800 dark:text-indigo-300'>
                <Badge tone='indigo'>{t('workspace.addressesInfoBadge')}</Badge>
                {t('workspace.addressesInfo')}
            </div>

            <Card>
                <CardHeader
                    title={t('workspace.addresses')}
                    actions={
                        canManage && (
                            <Button
                                size='sm'
                                onClick={() => setDialog('new')}
                            >
                                {t('workspace.newAddress')}
                            </Button>
                        )
                    }
                />
                {addresses.isLoading ? (
                    <PageLoader />
                ) : (addresses.data ?? []).length === 0 ? (
                    <EmptyState
                        title={t('workspace.noAddresses')}
                        description={
                            canManage
                                ? t('workspace.noAddressesManage')
                                : t('workspace.noAddressesNoManage')
                        }
                    />
                ) : (
                    <CardBody className='p-0'>
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                        <th className='px-4 py-2.5'>{t('workspace.addressCol')}</th>
                                        <th className='px-4 py-2.5'>{t('workspace.countryCol')}</th>
                                        <th className='px-4 py-2.5' />
                                    </tr>
                                </thead>
                                <tbody>
                                    {(addresses.data ?? []).map(a => (
                                        <tr
                                            key={a.id}
                                            className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                        >
                                            <td className='px-4 py-2.5'>
                                                <span className='font-medium text-slate-800 dark:text-slate-100'>
                                                    {a.street}{' '}
                                                    {a.streetNumber}
                                                    {a.isDefault && (
                                                        <span className='ml-2'>
                                                            <Badge tone='indigo'>
                                                                {t('workspace.defaultBadge')}
                                                            </Badge>
                                                        </span>
                                                    )}
                                                </span>
                                                {a.additionalInfo && (
                                                    <span className='block text-xs text-slate-500 dark:text-slate-400'>
                                                        {a.additionalInfo}
                                                    </span>
                                                )}
                                                <span className='block text-slate-500 dark:text-slate-400'>
                                                    {a.postalCode} {a.city}
                                                </span>
                                            </td>
                                            <td className='px-4 py-2.5 text-slate-500 dark:text-slate-400'>
                                                {a.country}
                                            </td>
                                            <td className='px-4 py-2.5 text-right'>
                                                {canManage && (
                                                    <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        onClick={() =>
                                                            setDialog(a)
                                                        }
                                                    >
                                                        {t('common.edit')}
                                                    </Button>
                                                )}
                                                {myRole === 'OWNER' && (
                                                    <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        onClick={() =>
                                                            setRemoveIntent(a)
                                                        }
                                                    >
                                                        {t('common.delete')}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardBody>
                )}
            </Card>

            <AddressDialog
                value={dialog}
                workspaceId={workspaceId}
                onClose={() => setDialog(null)}
            />

            <ConfirmDialog
                open={removeIntent !== null}
                message={
                    removeIntent
                        ? t('workspace.addressDeleteConfirm', {
                              street: removeIntent.street,
                              number: removeIntent.streetNumber,
                              zip: removeIntent.postalCode,
                              city: removeIntent.city,
                          })
                        : ''
                }
                confirmLabel={t('common.delete')}
                loading={remove.isPending}
                onClose={() => setRemoveIntent(null)}
                onConfirm={() => removeIntent && removeAddress(removeIntent)}
            />
        </div>
    )
}

function AddressDialog({
    value,
    workspaceId,
    onClose,
}: {
    value: Address | 'new' | null
    workspaceId: number
    onClose: () => void
}) {
    const t = useT()
    const { create, update } = useAddressMutations(workspaceId)
    const isNew = value === 'new'

    const [initializedFor, setInitializedFor] = useState<
        number | 'new' | null
    >(null)
    const [street, setStreet] = useState('')
    const [streetNumber, setStreetNumber] = useState('')
    const [additionalInfo, setAdditionalInfo] = useState('')
    const [postalCode, setPostalCode] = useState('')
    const [city, setCity] = useState('')
    const [country, setCountry] = useState('')
    const [isDefault, setIsDefault] = useState(false)

    const key = value === null ? null : value === 'new' ? 'new' : value.id
    // Reset synchron im Render; bei Schluss auch Key zurücksetzen, damit
    // 'new' nach dem Anlegen beim nächsten Öffnen wieder frisch ist.
    if (key === null) {
        if (initializedFor !== null) setInitializedFor(null)
    } else if (initializedFor !== key) {
        setInitializedFor(key)
        const a = value === 'new' || value === null ? null : value
        setStreet(a?.street ?? '')
        setStreetNumber(a?.streetNumber ?? '')
        setAdditionalInfo(a?.additionalInfo ?? '')
        setPostalCode(a?.postalCode ?? '')
        setCity(a?.city ?? '')
        setCountry(a?.country ?? t('workspace.countryDefault'))
        setIsDefault(a?.isDefault ?? false)
    }

    const body = (): AddressBody => ({
        street: street.trim(),
        streetNumber: streetNumber.trim(),
        additionalInfo: additionalInfo.trim() || undefined,
        postalCode: postalCode.trim(),
        city: city.trim(),
        country: country.trim(),
        isDefault,
    })

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        const onSuccess = () => {
            toast.success(
                isNew
                    ? t('workspace.addressCreated')
                    : t('workspace.addressUpdated'),
            )
            onClose()
        }
        const onError = (err: unknown) => toast.error(errText(err))
        if (isNew) {
            create.mutate(body(), { onSuccess, onError })
        } else if (value !== null) {
            update.mutate({ id: value.id, ...body() }, { onSuccess, onError })
        }
    }

    return (
        <Dialog
            open={value !== null}
            onClose={onClose}
            title={
                isNew
                    ? t('workspace.createAddressTitle')
                    : t('workspace.editAddressTitle')
            }
        >
            <form onSubmit={submit} className='space-y-4'>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                    <Field label={t('workspace.street')} className='sm:col-span-2'>
                        <Input
                            value={street}
                            onChange={e => setStreet(e.target.value)}
                            required
                            maxLength={191}
                        />
                    </Field>
                    <Field label={t('workspace.streetNumber')}>
                        <Input
                            value={streetNumber}
                            onChange={e => setStreetNumber(e.target.value)}
                            required
                            maxLength={32}
                        />
                    </Field>
                </div>
                <Field label={t('workspace.additionalInfo')}>
                    <Input
                        value={additionalInfo}
                        onChange={e => setAdditionalInfo(e.target.value)}
                        placeholder={t('workspace.additionalInfoPlaceholder')}
                        maxLength={500}
                    />
                </Field>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                    <Field label={t('workspace.postalCode')}>
                        <Input
                            value={postalCode}
                            onChange={e => setPostalCode(e.target.value)}
                            required
                            maxLength={32}
                        />
                    </Field>
                    <Field label={t('workspace.city')}>
                        <Input
                            value={city}
                            onChange={e => setCity(e.target.value)}
                            required
                            maxLength={191}
                        />
                    </Field>
                    <Field label={t('workspace.country')}>
                        <Input
                            value={country}
                            onChange={e => setCountry(e.target.value)}
                            required
                            maxLength={191}
                        />
                    </Field>
                </div>
                <label className='flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300'>
                    <input
                        type='checkbox'
                        checked={isDefault}
                        onChange={e => setIsDefault(e.target.checked)}
                        className='h-4 w-4 rounded border-slate-300 dark:border-slate-600 accent-indigo-700 dark:accent-indigo-500'
                    />
                    {t('workspace.setDefault')}
                </label>
                <div className='flex justify-end gap-2'>
                    <Button type='button' variant='secondary' onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type='submit'
                        loading={create.isPending || update.isPending}
                    >
                        {t('common.save')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}
